import type { CropSpriteKey } from '@/sprites/crops';
import type { Season } from '@/time/clock';

/**
 * Crop content.
 *
 * Declarative so a new plant is an entry here and nothing else. Growth is
 * expressed as days per stage rather than a total, because the visible stage
 * is what the player reads off the field — "that will be ready tomorrow" has
 * to be true.
 */

export type CropId = 'turnip' | 'tomato' | 'potato' | 'pumpkin';

export interface CropDefinition {
  readonly id: CropId;
  readonly name: string;
  /** Seasons the crop will grow in. Out of season it simply stops. */
  readonly seasons: readonly Season[];
  /**
   * Watered days needed to leave each stage. Length is the number of growing
   * stages; the crop is ripe once it passes the last one.
   */
  readonly stageDays: readonly number[];
  /** Sprite for the ripe plant; earlier stages are shared. */
  readonly ripeSprite: CropSpriteKey;
  readonly yieldAmount: number;
  /**
   * Stage the plant returns to after harvest, for crops that bear more than
   * once. Undefined means the plant is spent and the tile clears.
   */
  readonly regrowToStage?: number;
  readonly sellPrice: number;
}

export const CROPS: Readonly<Record<CropId, CropDefinition>> = {
  turnip: {
    id: 'turnip',
    name: 'Turnip',
    seasons: ['spring'],
    // Deliberately the fastest crop: a new player should harvest something
    // before they lose interest in watering.
    stageDays: [1, 1, 2],
    ripeSprite: 'turnipRipe',
    yieldAmount: 1,
    sellPrice: 35,
  },
  tomato: {
    id: 'tomato',
    name: 'Tomato',
    seasons: ['spring', 'summer'],
    stageDays: [2, 2, 3],
    ripeSprite: 'tomatoRipe',
    yieldAmount: 1,
    // Bears repeatedly, so a planted tomato keeps paying for the walk out.
    regrowToStage: 2,
    sellPrice: 60,
  },
  potato: {
    id: 'potato',
    name: 'Potato',
    seasons: ['spring', 'autumn'],
    stageDays: [2, 2, 2],
    ripeSprite: 'potatoRipe',
    yieldAmount: 2,
    sellPrice: 45,
  },
  pumpkin: {
    id: 'pumpkin',
    name: 'Pumpkin',
    seasons: ['autumn'],
    stageDays: [3, 3, 4],
    ripeSprite: 'pumpkinRipe',
    yieldAmount: 1,
    sellPrice: 160,
  },
};

/** Total growing stages, ripe excluded. */
export function growingStages(crop: CropDefinition): number {
  return crop.stageDays.length;
}

/** Days from planting to ripe, assuming it is watered every day. */
export function daysToRipe(crop: CropDefinition): number {
  return crop.stageDays.reduce((total, days) => total + days, 0);
}

export function isInSeason(crop: CropDefinition, season: Season): boolean {
  return crop.seasons.includes(season);
}

export function isCropId(value: unknown): value is CropId {
  return typeof value === 'string' && value in CROPS;
}
